import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import CombinedCommentsWidget from '../../components/dashboard/CombinedCommentsWidget';

import StatusBadge from '../../components/ui/StatusBadge';
import CapacityBar, { CapacityBarMini } from '../../components/ui/CapacityBar';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import DailyRoutinesWidget from '../../components/dashboard/DailyRoutinesWidget';
import { db, COLLECTIONS } from '../../firebase/config';
import ReactDOMServer from 'react-dom/server';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  Users, 
  FolderOpen, 
  CheckSquare, 
  AlertTriangle,
  AlertTriangle as AlertIcon,
  Clock,
  TrendingUp,
  Activity,
  Calendar,
  X,
  Plus,
  FolderPlus,
  Folder,
  FileText,
  Globe,
  Flag,
  Building2,
  User,
  Edit,
  AlertCircle,
  RotateCcw,
  Sun,
  Moon,
  Trash2,
  MessageSquare,
  CheckCircle,
  Award,
  Download,
  Brain,
  Zap,
  UserCheck,
  UserX,
  RefreshCw,
  Target
} from 'lucide-react';
import TabsComponent from '../../components/ui/TabsComponent';
import { useNavigate } from 'react-router-dom';
import SimpleProjectForm from '../../components/SimpleProjectForm';
import SimpleTaskForm from '../../components/SimpleTaskForm';
import { checkAndCreateDailyTasks, getTodayDailyTasks, getDailyRoutineStats, cleanupOrphanedTasks } from '../../utils/dailyRoutineScheduler';
import { renderTasksContent, renderProjectsContent, renderTeamsContent, renderDepartmentsContent } from '../../components/dashboard/InlineContent';

const OrgAdminDashboard = ({ activeTab: externalActiveTab, setActiveTab: externalSetActiveTab }) => {
  const { currentUser } = useAuth();
  const { tasks = [], projects = [], users = [], notifications = [], departments = [], teams = [], comments = [],
          updateProject, updateTask, updateUser, createProject, createTask, deleteProject, deleteTask, createDepartment,
          createTeam, updateTeam, deleteTeam } = useData();
  const navigate = useNavigate();

  // Internal state for tabs if external state is not provided
  const [internalActiveTab, setInternalActiveTab] = useState('management');
  const activeTab = externalActiveTab || internalActiveTab;
  const setActiveTab = externalSetActiveTab || setInternalActiveTab;

  // 
  //  for filters
  const [filterProjectStatus, setFilterProjectStatus] = useState('all');
  const [filterTaskStatus, setFilterTaskStatus] = useState('all');
  const [filterTaskPriority, setFilterTaskPriority] = useState('all');
  const [filterProjectScope, setFilterProjectScope] = useState('all');
  const [showCompletedProjects, setShowCompletedProjects] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  
  // State for inline content display
  const [selectedContent, setSelectedContent] = useState(null); // 'tasks', 'projects', 'teams', 'departments'
  const [selectedTab, setSelectedTab] = useState('management'); // 'management', 'personal'
  useEffect(() => {
    setSelectedContent(null);
    setSelectedTab(activeTab);
  }, [activeTab]);
  // State for daily routines
  const [dailyTasks, setDailyTasks] = useState([]);
  const [dailyRoutineStats, setDailyRoutineStats] = useState(null);
  const [loadingDailyTasks, setLoadingDailyTasks] = useState(true);
  const [dailyRoutines, setDailyRoutines] = useState([]);
  const [loadingRoutines, setLoadingRoutines] = useState(true);

  // State for modals
  const [showEditProject, setShowEditProject] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewDepartment, setShowNewDepartment] = useState(false);
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [showPriorityTasks, setShowPriorityTasks] = useState(null);
  const [showProjectTasks, setShowProjectTasks] = useState(null);
  const [newProjectTasks, setNewProjectTasks] = useState([]);
  const [topPerformersView, setTopPerformersView] = useState('month');

  // State for new forms
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    scope: 'department',
    priority: 'important_not_urgent',
    startDate: '',
    endDate: '',
    teamMembers: []
  });
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigneeId: '',
    priority: 'important_not_urgent',
    dueDate: '',
    estimate: 0,
    projectId: ''
  });
  const [newDepartment, setNewDepartment] = useState({
    name: '',
    description: '',
    managerId: '',
    isActive: true
  });
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    departmentId: '',
    managerId: '',
    memberIds: [],
    isActive: true
  });

  // Function to load daily routines
  const loadDailyRoutines = async () => {
    try {
      setLoadingRoutines(true);
      const routinesRef = collection(db, COLLECTIONS.DAILY_ROUTINES || 'dailyRoutines');
      const snapshot = await getDocs(routinesRef);
      const routinesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDailyRoutines(routinesData.filter(r => r.isActive)); // רק שגרות פעילות
    } catch (error) {
      console.error('Error loading routines:', error);
    } finally {
      setLoadingRoutines(false);
    }
  };

  // Function to delete routine
  const handleDeleteRoutine = async (routine) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את השגרה "${routine.name}"?`)) {
      try {
        await deleteDoc(doc(db, COLLECTIONS.DAILY_ROUTINES || 'dailyRoutines', routine.id));
        
        // מחיקת כל המשימות הקשורות לשגרה
        const tasksToDelete = tasks.filter(t => t.routineId === routine.id);
        for (const task of tasksToDelete) {
          await deleteTask(task.id);
        }
        
        // עדכון ה-state המקומי
        setDailyRoutines(prev => prev.filter(r => r.id !== routine.id));
        setDailyTasks(prev => prev.filter(t => t.routineId !== routine.id));
        
        alert(`השגרה "${routine.name}" נמחקה בהצלחה`);
      } catch (error) {
        console.error('Error deleting routine:', error);
        alert('שגיאה במחיקת השגרה');
      }
    }
  };

  // Function to delete daily task
  const handleDeleteDailyTask = async (taskId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק משימה זו?')) {
      try {
        await deleteTask(taskId);
        setDailyTasks(prev => prev.filter(t => t.id !== taskId));
        alert('המשימה נמחקה בהצלחה');
      } catch (error) {
        console.error('Error deleting task:', error);
        alert('שגיאה במחיקת המשימה');
      }
    }
  };

  // Load daily routines data on component mount
  useEffect(() => {
    const loadDailyRoutinesData = async () => {
      try {
        setLoadingDailyTasks(true);
        
        // טען גם את השגרות עצמן
        await loadDailyRoutines();
        
        // First cleanup any orphaned tasks
        await cleanupOrphanedTasks();
        
        // Check and create daily tasks if needed
        await checkAndCreateDailyTasks();
        
        // Get today's daily tasks
        const todayTasksResult = await getTodayDailyTasks();
        if (todayTasksResult.success) {
          setDailyTasks(todayTasksResult.tasks);
        }
        
        // Get daily routine stats
        const statsResult = await getDailyRoutineStats();
        if (statsResult.success) {
          setDailyRoutineStats(statsResult.stats);
        }
        
      } catch (error) {
        console.error('Error loading daily routines data:', error);
      } finally {
        setLoadingDailyTasks(false);
      }
    };

    loadDailyRoutinesData();
  }, []);

  // Function to mark daily task as completed
  const markDailyTaskCompleted = async (taskId) => {
    try {
      await updateTask(taskId, { status: 'הושלם' });
      
      // Refresh daily tasks
      const todayTasksResult = await getTodayDailyTasks();
      if (todayTasksResult.success) {
        setDailyTasks(todayTasksResult.tasks);
      }
      
      // Refresh stats
      const statsResult = await getDailyRoutineStats();
      if (statsResult.success) {
        setDailyRoutineStats(statsResult.stats);
      }
      
      // Show success message
      alert('המשימה סומנה כמושלמת בהצלחה!');
    } catch (error) {
      console.error('Error marking daily task as completed:', error);
      alert('שגיאה בעדכון סטטוס המשימה');
    }
  };

  // Function to handle box clicks
  const handleBoxClick = (contentType, tabType) => {
    setSelectedContent(contentType);
    setSelectedTab(tabType);
  };

  // Helper functions
  const shouldHideTask = (task) => {
    // הסתר משימות שנוצרו משגרות יומיות - הן צריכות להופיע רק בעמוד השגרות
    if (task.isDailyRoutine || task.routineId || task.createdFrom === 'daily_routine') {
      return true;
    }
    
    // בדיקה נוספת - משימות שהשם שלהן מכיל תאריך (סימן למשימת שגרה)
    if (task.title && task.title.includes(' - ') && task.title.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
      return true;
    }
    
    const taskProject = task.projectId ? projects.find(p => p.id === task.projectId) : null;
    const hidePersonalTask = task.scope === 'personal' && task.assigneeId !== currentUser?.id && task.createdBy !== currentUser?.id;
    const hidePersonalProjectTask = taskProject && taskProject.scope === 'personal' && taskProject.assigneeId !== currentUser?.id && taskProject.createdBy !== currentUser?.id;
    return hidePersonalTask || hidePersonalProjectTask;
  };

  const getProjectStatus = (project) => {
    if (!project) return 'לא ידוע';
    const projectTasks = tasks.filter(t => t.projectId === project.id);
    if (projectTasks.length === 0) return 'ממתין לעבודה';
    const completedTasks = projectTasks.filter(t => t.status === 'הושלם').length;
    const totalTasks = projectTasks.length;
    if (completedTasks === 0) return 'ממתין לעבודה';
    if (completedTasks === totalTasks) return 'פרויקט הושלם';
    return 'פרויקט בתהליך';
  };

  // Filter projects and tasks
  const filteredDashboardProjects = projects.filter(project => {
    let hasAccess = currentUser?.role === 'org_admin' || currentUser?.role === 'ceo';
    if (!hasAccess) return false;
    
    const matchesScope = filterProjectScope === 'all' || project.scope === filterProjectScope;
    const projectStatus = getProjectStatus(project);
    const matchesStatus = filterProjectStatus === 'all' || 
                         (filterProjectStatus === 'completed' && projectStatus === 'פרויקט הושלם') ||
                         projectStatus === filterProjectStatus;
    
    if (showCompletedProjects && projectStatus !== 'פרויקט הושלם') return false;
    if (!showCompletedProjects && projectStatus === 'פרויקט הושלם') return false;
    
    return matchesScope && matchesStatus;
  });

  const filteredDashboardTasks = tasks.filter(task => {
    if (shouldHideTask(task)) return false;
    
    // Additional filter: Only show organizational tasks for org admin
    if (currentUser?.role === 'org_admin') {
      // Show only organizational tasks (not personal)
      if (task.scope === 'personal' && task.assigneeId !== currentUser?.id && task.createdBy !== currentUser?.id) {
        return false;
      }
    }
    
    const matchesStatus = filterTaskStatus === 'all' || task.status === filterTaskStatus;
    const matchesPriority = filterTaskPriority === 'all' || task.priority === filterTaskPriority;
    if (showCompletedTasks && task.status !== 'הושלם') return false;
    if (!showCompletedTasks && task.status === 'הושלם') return false;
    return matchesStatus && matchesPriority;
  });

  const unacknowledgedTasks = tasks.filter(task => {
    if (!task || task.acknowledged) return false;
    return (task.assigneeId === currentUser?.id || 
           (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(currentUser?.id)) ||
           task.createdBy === currentUser?.id) && !shouldHideTask(task);
  }) || [];

  const atRiskTasks = tasks.filter(task => {
    if (!task || (task.status !== 'בסיכון' && task.status !== 'חסום')) return false;
    return (task.assigneeId === currentUser?.id || 
           (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(currentUser?.id)) ||
           task.createdBy === currentUser?.id) && !shouldHideTask(task);
  }) || [];

  const unreadNotifications = notifications.filter(n => {
    if (!n || n.read) return false;
    return n.userId === currentUser?.id || n.createdBy === currentUser?.id;
  }) || [];

  // Priority matrix
  const priorityMatrix = [
    { 
      key: 'important_urgent', 
      label: 'חשוב ודחוף', 
      color: 'bg-red-600', 
      tasks: filteredDashboardTasks.filter(t => t.priority === 'important_urgent')
    },
    { 
      key: 'important_not_urgent', 
      label: 'חשוב ולא דחוף', 
      color: 'bg-blue-600', 
      tasks: filteredDashboardTasks.filter(t => t.priority === 'important_not_urgent')
    },
    { 
      key: 'not_important_urgent', 
      label: 'דחוף ולא חשוב', 
      color: 'bg-yellow-600', 
      tasks: filteredDashboardTasks.filter(t => t.priority === 'not_important_urgent')
    },
    { 
      key: 'not_important_not_urgent', 
      label: 'לא חשוב ולא דחוף', 
      color: 'bg-gray-600', 
      tasks: filteredDashboardTasks.filter(t => t.priority === 'not_important_not_urgent')
    }
  ];

  // Stats card component
  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600'
    };

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-2xl font-bold mt-1">{value || 0}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    );
  };

  // Render inline content based on selection
  const renderInlineContent = () => {
    if (!selectedContent) return null;

    const isPersonal = selectedTab === 'personal';
    
    switch (selectedContent) {
      case 'tasks':
        return renderTasksContent(tasks, users, currentUser, isPersonal, setSelectedContent, navigate);
      case 'projects':
        return renderProjectsContent(projects, tasks, users, currentUser, isPersonal, setSelectedContent, navigate);
      case 'teams':
        return renderTeamsContent(teams, users, tasks, setSelectedContent, navigate);
      case 'departments':
        return renderDepartmentsContent(departments, users, tasks, setSelectedContent, navigate, currentUser);
      default:
        return null;
    }
  };
// פונקציה ליצוא CSV
  const exportToCSV = (data, filename) => {
    const csvContent = data.map(row => Object.values(row).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

// מצא את הפונקציה calculateChartData ותחליף את החלק של מגמות 30 יום אחרונים:

// חישוב נתונים לגרפים
const calculateChartData = () => {
  // התפלגות משימות לפי מחלקות
  const tasksByDepartment = departments.map(dept => {
    const deptTasks = tasks.filter(t => t.departmentId === dept.id);
    return {
      name: dept.name,
      value: deptTasks.length,
      completed: deptTasks.filter(t => t.status === 'הושלם').length
    };
  });

  // ביצועי מחלקות
  const departmentPerformance = departments.map(dept => {
    const deptTasks = tasks.filter(t => t.departmentId === dept.id);
    const completedTasks = deptTasks.filter(t => t.status === 'הושלם').length;
    const performance = deptTasks.length > 0 ? (completedTasks / deptTasks.length) * 100 : 0;
    
    return {
      department: dept.name,
      performance: Math.round(performance),
      tasks: deptTasks.length,
      projects: projects.filter(p => p.departmentId === dept.id).length
    };
  });

  // מגמות 30 יום אחרונים
  const last30Days = [];
  for (let i = 29; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    
    const dayTasks = tasks.filter(t => {
      if (!t.createdAt) return false;
      
      let taskDate;
      if (t.createdAt.toDate) {
        taskDate = t.createdAt.toDate();
      } else if (typeof t.createdAt === 'string') {
        taskDate = new Date(t.createdAt);
      } else if (t.createdAt instanceof Date) {
        taskDate = t.createdAt;
      } else {
        return false;
      }
      
      return taskDate.toDateString() === date.toDateString();
    });
    
    last30Days.push({
      date: date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' }),
      tasks: dayTasks.length,
      completed: dayTasks.filter(t => t.status === 'הושלם').length
    });
  }

  // Heat Map - פעילות אמיתית לפי ימים ושעות
  const activityHeatMap = [];
  const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
  const hours = ['06:00', '09:00', '12:00', '15:00', '18:00', '21:00'];
  
  days.forEach((day, dayIndex) => {
    hours.forEach((hour, hourIndex) => {
      const tasksInTimeSlot = tasks.filter(task => {
        if (!task.createdAt) return false;
        
        let taskDate;
        if (task.createdAt.toDate) {
          taskDate = task.createdAt.toDate();
        } else if (typeof task.createdAt === 'string') {
          taskDate = new Date(task.createdAt);
        } else if (task.createdAt instanceof Date) {
          taskDate = task.createdAt;
        } else {
          return false;
        }
        
        const taskDay = taskDate.getDay();
        const taskHour = taskDate.getHours();
        const hourNum = parseInt(hour.split(':')[0]);
        
        return taskDay === dayIndex && taskHour >= hourNum && taskHour < hourNum + 3;
      }).length;
      
      activityHeatMap.push({
        day,
        hour,
        value: tasksInTimeSlot
      });
    });
  });

  // פרויקטים לפי סטטוס
  const projectsByStatus = [
    { name: 'חדש', value: projects.filter(p => getProjectStatus(p) === 'ממתין לעבודה').length, color: '#3B82F6' },
    { name: 'בתהליך', value: projects.filter(p => getProjectStatus(p) === 'פרויקט בתהליך').length, color: '#10B981' },
    { name: 'הושלם', value: projects.filter(p => getProjectStatus(p) === 'פרויקט הושלם').length, color: '#6B7280' },
  ];

  // נתונים ל-Radar Chart
  const radarData = departments.slice(0, 5).map(dept => {
    const deptUsers = users.filter(u => u.departmentId === dept.id);
    const deptTasks = tasks.filter(t => t.departmentId === dept.id);
    const deptProjects = projects.filter(p => p.departmentId === dept.id);
    const completedTasks = deptTasks.filter(t => t.status === 'הושלם');
    const overdueTasks = deptTasks.filter(t => 
      t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'הושלם'
    );
    
    const efficiency = deptTasks.length > 0 
      ? Math.round(((completedTasks.length - overdueTasks.length) / deptTasks.length) * 100)
      : 0;
    
    return {
      department: dept.name,
      משימות: deptTasks.length,
      פרויקטים: deptProjects.length,
      עובדים: deptUsers.length,
      השלמה: Math.round((completedTasks.length / (deptTasks.length || 1)) * 100),
      יעילות: Math.max(0, efficiency)
    };
  });

  // חישוב משימות באיחור
  const overdueTasks = tasks.filter(task => 
    task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'הושלם'
  );
  
  // חישוב פרויקטים בעיכוב
  const delayedProjects = projects.filter(project => 
    project.endDate && new Date(project.endDate) < new Date() && 
    getProjectStatus(project) !== 'פרויקט הושלם'
  );
  
  // נתונים לכרטיסי KPI נוספים
  const additionalKPIs = {
    totalTasks: tasks.length,
    openTasks: tasks.filter(t => t.status === 'ממתין לעבודה').length,
    inProgressTasks: tasks.filter(t => t.status === 'במסלול').length,
    completedTasks: tasks.filter(t => t.status === 'הושלם').length,
    overdueTasks: overdueTasks.length,
    totalProjects: projects.length,
    activeProjects: projects.filter(p => getProjectStatus(p) !== 'פרויקט הושלם').length,
    completedProjects: projects.filter(p => getProjectStatus(p) === 'פרויקט הושלם').length,
    delayedProjects: delayedProjects.length
  };
  
  return {
    tasksByDepartment,
    departmentPerformance,
    last30Days,
    activityHeatMap,
    projectsByStatus,
    radarData,
    additionalKPIs
  };
};
// נתונים לטבלאות
const getTableData = () => {
  // טבלת מחלקות
  const departmentsTable = departments.map(dept => {
    const deptManager = users.find(u => u.id === dept.managerId);
    const deptUsers = users.filter(u => u.departmentId === dept.id);
    const deptTasks = tasks.filter(t => t.departmentId === dept.id);
    const deptProjects = projects.filter(p => p.departmentId === dept.id);
    const completedTasks = deptTasks.filter(t => t.status === 'הושלם');
    
    return {
      name: dept.name,
      manager: deptManager?.name || 'לא מוגדר',
      employees: deptUsers.length,
      projects: deptProjects.length,
      tasks: deptTasks.length,
      completion: deptTasks.length > 0 ? Math.round((completedTasks.length / deptTasks.length) * 100) : 0
    };
  });

  // טבלת עובדים - עם חישוב ביצועים אמיתי
  const employeesTable = users.map(user => {
    const userTasks = tasks.filter(t => t.assigneeId === user.id || t.assigneeIds?.includes(user.id));
    const activeTasks = userTasks.filter(t => t.status !== 'הושלם');
    const completedTasks = userTasks.filter(t => t.status === 'הושלם');
    const overdueTasks = userTasks.filter(t => 
      t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'הושלם'
    );
    const userDept = departments.find(d => d.id === user.departmentId);
    const userRoutines = dailyTasks.filter(t => t.assigneeId === user.id);
    
    // חישוב ביצועים אמיתי
    const performance = userTasks.length > 0
      ? Math.round(((completedTasks.length - overdueTasks.length * 0.5) / userTasks.length) * 100)
      : 0;
    
    return {
      name: user.name,
      department: userDept?.name || 'ללא מחלקה',
      role: user.role,
      activeTasks: activeTasks.length,
      dailyRoutines: userRoutines.length,
      performance: Math.max(0, Math.min(100, performance))
    };
  });

  // טבלת פרויקטים
  const projectsTable = projects.map(project => {
    const projectDept = departments.find(d => d.id === project.departmentId);
    const projectTasks = tasks.filter(t => t.projectId === project.id);
    const completedTasks = projectTasks.filter(t => t.status === 'הושלם');
    const progress = projectTasks.length > 0 ? Math.round((completedTasks.length / projectTasks.length) * 100) : 0;
    
    const isDelayed = project.endDate && new Date(project.endDate) < new Date() && progress < 100;
    
    return {
      name: project.name,
      department: projectDept?.name || 'ללא מחלקה',
      status: getProjectStatus(project),
      progress,
      dueDate: project.endDate ? new Date(project.endDate).toLocaleDateString('he-IL') : 'לא מוגדר',
      tasks: projectTasks.length,
      isDelayed,
      team: project.teamMembers?.length || 0
    };
  });

  // טבלת משימות
  const tasksTable = tasks.map(task => {
    const assignee = users.find(u => u.id === task.assigneeId);
    const project = projects.find(p => p.id === task.projectId);
    const dept = departments.find(d => d.id === task.departmentId);
    
    const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'הושלם';
    
    return {
      title: task.title,
      assignee: assignee?.name || 'לא מוקצה',
      project: project?.name || 'ללא פרויקט',
      department: dept?.name || 'ללא מחלקה',
      status: task.status,
      priority: task.priority,
      dueDate: task.dueDate ? new Date(task.dueDate).toLocaleDateString('he-IL') : 'לא מוגדר',
      isOverdue,
      estimate: task.estimate || 0,
      description: task.description?.substring(0, 50) + '...' || ''
    };
  });

  // טבלת שגרות - עם חישובים אמיתיים
  const routinesTable = dailyRoutines.map(routine => {
    const routineTasks = dailyTasks.filter(t => t.routineId === routine.id);
    const completedToday = routineTasks.filter(t => t.status === 'הושלם').length;
    const assignees = routine.assigneeIds?.map(id => users.find(u => u.id === id)?.name).filter(Boolean) || [];
    
    const dailyRate = routineTasks.length > 0 
      ? Math.round((completedToday / routineTasks.length) * 100) 
      : 0;
    
    // חישוב שבועי
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - 7);
    const weeklyTasks = tasks.filter(t => 
      t.routineId === routine.id && 
      t.createdAt && 
      new Date(t.createdAt) >= weekStart
    );
    const weeklyCompleted = weeklyTasks.filter(t => t.status === 'הושלם').length;
    const weeklyRate = weeklyTasks.length > 0 
      ? Math.round((weeklyCompleted / weeklyTasks.length) * 100)
      : 0;
    
    // חישוב חודשי
    const monthStart = new Date();
    monthStart.setDate(monthStart.getDate() - 30);
    const monthlyTasks = tasks.filter(t => 
      t.routineId === routine.id && 
      t.createdAt && 
      new Date(t.createdAt) >= monthStart
    );
    const monthlyCompleted = monthlyTasks.filter(t => t.status === 'הושלם').length;
    const monthlyRate = monthlyTasks.length > 0 
      ? Math.round((monthlyCompleted / monthlyTasks.length) * 100)
      : 0;
    
    return {
      name: routine.name,
      frequency: routine.frequency === 'daily' ? 'יומי' : 'שבועי',
      assignees: assignees.join(', ') || 'לא מוקצה',
      completedToday: `${completedToday}/${routineTasks.length}`,
      dailyRate,
      weeklyRate,
      monthlyRate
    };
  });

  return {
    departmentsTable,
    employeesTable,
    projectsTable,
    tasksTable,
    routinesTable
  };
};
  // Tabs configuration
  const tabs = (() => {
    const managementTab = {
      id: 'management',
      label: 'ניהול',
      icon: Building2,
      badge: departments.length.toString(),
      content: (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">ניהול החברה</h2>
            <p className="text-sm text-gray-600 mt-1">בחר קטגוריה כדי לראות את המידע הרלוונטי</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Departments Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('departments', 'management')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Building2 size={32} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">מחלקות</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">{departments.length}</p>
                  <p className="text-sm text-gray-600">מחלקות בחברה</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {departments.slice(0, 3).map(dept => (
                      <div key={dept.id} className="text-center py-1">
                        {dept.name}
                      </div>
                    ))}
                    {departments.length > 3 && (
                      <div className="text-center py-1 text-blue-600">
                        +{departments.length - 3} עוד
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Projects Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('projects', 'management')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">פרויקטים</h3>
                  <p className="text-3xl font-bold text-green-600 mb-2">
                    {(() => {
                      const managementProjects = projects.filter(p => p.scope === 'managerial' || p.scope === 'department');
                      return managementProjects.length;
                    })()}
                  </p>
                  <p className="text-sm text-gray-600">פרויקטים פעילים</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const managementProjects = projects.filter(p => p.scope === 'managerial' || p.scope === 'department');
                      const activeProjects = managementProjects.filter(p => {
                        const projectTasks = tasks.filter(t => t.projectId === p.id);
                        if (projectTasks.length === 0) return true;
                        const completedTasks = projectTasks.filter(t => t.status === 'הושלם');
                        return completedTasks.length < projectTasks.length;
                      });
                      const completedProjects = managementProjects.filter(p => {
                        const projectTasks = tasks.filter(t => t.projectId === p.id);
                        if (projectTasks.length === 0) return false;
                        const completedTasks = projectTasks.filter(t => t.status === 'הושלם');
                        return completedTasks.length === projectTasks.length;
                      });
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeProjects.length} פעילים
                          </div>
                          <div className="text-center py-1 text-green-600">
                            {completedProjects.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Tasks Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('tasks', 'management')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <CheckSquare size={32} className="text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">משימות</h3>
                  <p className="text-3xl font-bold text-purple-600 mb-2">
                    {(() => {
                      const managementTasks = tasks.filter(t => t.scope === 'managerial' || t.scope === 'department');
                      return managementTasks.length;
                    })()}
                  </p>
                  <p className="text-sm text-gray-600">משימות פעילות</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const managementTasks = tasks.filter(t => t.scope === 'managerial' || t.scope === 'department');
                      const activeTasks = managementTasks.filter(t => t.status !== 'הושלם');
                      const completedTasks = managementTasks.filter(t => t.status === 'הושלם');
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeTasks.length} פעילות
                          </div>
                          <div className="text-center py-1 text-purple-600">
                            {completedTasks.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Teams Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('teams', 'management')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <Users size={32} className="text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">צוותים</h3>
                  <p className="text-3xl font-bold text-orange-600 mb-2">{teams.length}</p>
                  <p className="text-sm text-gray-600">צוותים פעילים</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const activeTeams = teams.filter(t => t.isActive);
                      const inactiveTeams = teams.filter(t => !t.isActive);
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeTeams.length} פעילים
                          </div>
                          <div className="text-center py-1 text-orange-600">
                            {inactiveTeams.length} לא פעילים
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-medium mb-4">פעולות מהירות</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('/admin')}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                >
                  <Users size={16} />
                  ניהול מערכת
                </button>
                <button
                  onClick={() => navigate('/teams')}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                >
                  <Users size={16} />
                  ניהול צוותים
                </button>
                <button
                  onClick={() => navigate('/tasks')}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <CheckSquare size={16} />
                  ניהול משימות
                </button>
                <button
                  onClick={() => navigate('/projects')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <FolderOpen size={16} />
                  ניהול פרויקטים
                </button>
                <button
                  onClick={() => navigate('/departments')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Building2 size={16} />
                  ניהול מחלקות
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    };

    const personalTab = {
      id: 'personal',
      label: 'אישי',
      icon: User,
      badge: (() => {
        const personalProjects = projects.filter(p => p.scope === 'personal');
        const personalTasks = tasks.filter(t => t.scope === 'personal');
        return (personalProjects.length + personalTasks.length).toString();
      })(),
      content: (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">פרויקטים ומשימות אישיים</h2>
            <p className="text-sm text-gray-600 mt-1">ניהול הפרויקטים והמשימות האישיים שלך</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Projects Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('projects', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">פרויקטים אישיים</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">
                    {(() => {
                      const personalProjects = projects.filter(p => p.scope === 'personal');
                      return personalProjects.length;
                    })()}
                  </p>
                  <p className="text-sm text-gray-600">פרויקטים אישיים</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const personalProjects = projects.filter(p => p.scope === 'personal');
                      const activeProjects = personalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length === 0 || done < pts.length;
                      });
                      const completedProjects = personalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length > 0 && done === pts.length;
                      });
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeProjects.length} פעילים
                          </div>
                          <div className="text-center py-1 text-blue-600">
                            {completedProjects.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Personal Tasks Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('tasks', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <CheckSquare size={32} className="text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">משימות אישיות</h3>
                 <p className="text-3xl font-bold text-purple-600 mb-2">
  {(() => {
    const personalTasks = tasks.filter(t => t.scope === 'personal');
    return personalTasks.length;
  })()}
</p>
                  <p className="text-sm text-gray-600">משימות אישיות</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const personalTasks = tasks.filter(t => t.scope === 'personal');
                      const activeTasks = personalTasks.filter(t => t.status !== 'הושלם');
                      const completedTasks = personalTasks.filter(t => t.status === 'הושלם');
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeTasks.length} פעילות
                          </div>
                          <div className="text-center py-1 text-purple-600">
                            {completedTasks.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-medium mb-4">פעולות מהירות</h3>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => setShowNewProject(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <FolderOpen size={16} />
                  פרויקט חדש
                </button>
                <button 
                  onClick={() => setShowNewTask(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <CheckSquare size={16} />
                  משימה חדשה
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    };
// Helper function to generate smart insights  
function generateSmartInsights() {
  const insights = [];
  const now = new Date();
  
  // 1. משימות באיחור
  const overdueTasks = tasks.filter(t => {
    if (!t.dueDate || t.status === 'הושלם') return false;
    const dueDate = new Date(t.dueDate);
    return dueDate < now;
  });
  
  if (overdueTasks.length > 0) {
    insights.push({
      title: `${overdueTasks.length} משימות באיחור`,
      description: `יש ${overdueTasks.length} משימות שחרגו מתאריך היעד שלהן. כדאי לתת להן עדיפות גבוהה.`,
      severity: 'critical',
      icon: <AlertCircle className="w-5 h-5" />,
      action: 'צפה במשימות'
    });
  }
  
  // 2. משימות קריטיות
  const criticalTasks = tasks.filter(t => 
    t.priority === 'important_urgent' && 
    t.status !== 'הושלם'
  );
  
  if (criticalTasks.length > 3) {
    insights.push({
      title: 'עומס משימות קריטיות',
      description: `יש ${criticalTasks.length} משימות בעדיפות "חשוב ודחוף". מומלץ להקצות משאבים נוספים.`,
      severity: 'warning',
      icon: <Zap className="w-5 h-5" />,
      action: 'נהל עדיפויות'
    });
  }
  
  // 3. משימות תקועות
  const stuckTasks = tasks.filter(t => {
    if (t.status === 'הושלם' || !t.updatedAt) return false;
    const lastUpdate = new Date(t.updatedAt);
    const daysSinceUpdate = (now - lastUpdate) / (1000 * 60 * 60 * 24);
    return daysSinceUpdate > 7;
  });
  
  if (stuckTasks.length > 0) {
    insights.push({
      title: `${stuckTasks.length} משימות תקועות`,
      description: 'משימות אלו לא עודכנו מעל שבוע. כדאי לבדוק את הסטטוס שלהן.',
      severity: 'warning',
      icon: <Clock className="w-5 h-5" />,
      action: 'בדוק סטטוס'
    });
  }
  
  // 4. עובדים עמוסים
  const overloadedEmployees = users.filter(user => {
    const userTasks = tasks.filter(t => 
      (t.assigneeId === user.id || t.assigneeIds?.includes(user.id)) &&
      t.status !== 'הושלם'
    );
    return userTasks.length > 10;
  });
  
  if (overloadedEmployees.length > 0) {
    insights.push({
      title: `${overloadedEmployees.length} עובדים עמוסים`,
      description: 'עובדים אלו מוקצים ליותר מ-10 משימות פעילות. שקול חלוקה מחדש.',
      severity: 'warning',
      icon: <Users className="w-5 h-5" />,
      action: 'אזן עומסים'
    });
  }
  
  // 5. עובדים פנויים
  const underutilizedEmployees = users.filter(user => {
    const userTasks = tasks.filter(t => 
      (t.assigneeId === user.id || t.assigneeIds?.includes(user.id)) &&
      t.status !== 'הושלם'
    );
    return userTasks.length < 3;
  });
  
  if (underutilizedEmployees.length > 0) {
    insights.push({
      title: `${underutilizedEmployees.length} עובדים פנויים`,
      description: 'לעובדים אלו יש פחות מ-3 משימות פעילות. ניתן להקצות להם משימות נוספות.',
      severity: 'info',
      icon: <UserCheck className="w-5 h-5" />,
      action: 'הקצה משימות'
    });
  }
  
  // 6. פרויקטים ללא התקדמות
  const stagnantProjects = projects.filter(p => {
    if (getProjectStatus(p) === 'פרויקט הושלם') return false;
    const projectTasks = tasks.filter(t => t.projectId === p.id);
    const recentlyUpdated = projectTasks.some(t => {
      if (!t.updatedAt) return false;
      const lastUpdate = new Date(t.updatedAt);
      const daysSinceUpdate = (now - lastUpdate) / (1000 * 60 * 60 * 24);
      return daysSinceUpdate < 7;
    });
    return !recentlyUpdated;
  });
  
  if (stagnantProjects.length > 0) {
    insights.push({
      title: `${stagnantProjects.length} פרויקטים ללא התקדמות`,
      description: 'פרויקטים אלו לא עודכנו בשבוע האחרון. בדוק את הסטטוס שלהם.',
      severity: 'warning',
      icon: <FolderOpen className="w-5 h-5" />,
      action: 'צפה בפרויקטים'
    });
  }
  
  // 7. פרויקטים לפני דדליין
  const weekFromNow = new Date();
  weekFromNow.setDate(weekFromNow.getDate() + 7);
  
  const urgentProjects = projects.filter(p => {
    if (!p.dueDate || getProjectStatus(p) === 'פרויקט הושלם') return false;
    const dueDate = new Date(p.dueDate);
    return dueDate <= weekFromNow && dueDate >= now;
  });
  
  if (urgentProjects.length > 0) {
    insights.push({
      title: `${urgentProjects.length} פרויקטים בדדליין השבוע`,
      description: 'פרויקטים אלו אמורים להסתיים בשבוע הקרוב. וודא שהם במסלול.',
      severity: 'warning',
      icon: <Calendar className="w-5 h-5" />,
      action: 'תעדף פרויקטים'
    });
  }
  
  // 8. עובד החודש
  const monthStart = new Date();
  monthStart.setDate(1);
  
  const topPerformer = users.map(user => {
    const completedTasks = tasks.filter(t => 
      (t.assigneeId === user.id || t.assigneeIds?.includes(user.id)) &&
      t.status === 'הושלם' &&
      t.completedAt && new Date(t.completedAt) >= monthStart
    ).length;
    return { user, completedTasks };
  }).sort((a, b) => b.completedTasks - a.completedTasks)[0];
  
  if (topPerformer && topPerformer.completedTasks > 0) {
    insights.push({
      title: 'עובד החודש',
      description: `${topPerformer.user.name} השלים ${topPerformer.completedTasks} משימות החודש. ביצועים מרשימים!`,
      severity: 'success',
      icon: <Award className="w-5 h-5" />,
      action: 'הוקרה'
    });
  }
  
  // 9. איזון מטריצת עדיפויות
  const priorityDistribution = {
    important_urgent: tasks.filter(t => t.priority === 'important_urgent').length,
    important_not_urgent: tasks.filter(t => t.priority === 'important_not_urgent').length,
    not_important_urgent: tasks.filter(t => t.priority === 'not_important_urgent').length,
    not_important_not_urgent: tasks.filter(t => t.priority === 'not_important_not_urgent').length
  };
  
  if (priorityDistribution.important_urgent > tasks.length * 0.4) {
    insights.push({
      title: 'יותר מדי משימות דחופות',
      description: 'מעל 40% מהמשימות מסומנות כ"חשוב ודחוף". שקול להגדיר מחדש את העדיפויות.',
      severity: 'info',
      icon: <Target className="w-5 h-5" />,
      action: 'אזן עדיפויות'
    });
  }
  
  // 10. עובדים לא פעילים
  const inactiveUsers = users.filter(user => {
    const recentActivity = tasks.some(t => {
      if ((t.assigneeId !== user.id && !t.assigneeIds?.includes(user.id)) || !t.updatedAt) return false;
      const lastUpdate = new Date(t.updatedAt);
      const daysSinceUpdate = (now - lastUpdate) / (1000 * 60 * 60 * 24);
      return daysSinceUpdate < 3;
    });
    return !recentActivity;
  });
  
  if (inactiveUsers.length > 0) {
    insights.push({
      title: `${inactiveUsers.length} עובדים לא פעילים`,
      description: 'עובדים אלו לא עדכנו משימות ב-3 הימים האחרונים.',
      severity: 'info',
      icon: <UserX className="w-5 h-5" />,
      action: 'בדוק סטטוס'
    });
  }
  
  // 11. משימות שחזרו לעבודה
  const reopenedTasks = tasks.filter(t => 
    t.status !== 'הושלם' && 
    t.previousStatus === 'הושלם'
  );
  
  if (reopenedTasks.length > 0) {
    insights.push({
      title: `${reopenedTasks.length} משימות נפתחו מחדש`,
      description: 'משימות אלו סומנו כהושלמו אך חזרו למצב פעיל. כדאי לבדוק למה.',
      severity: 'warning',
      icon: <RefreshCw className="w-5 h-5" />,
      action: 'חקור סיבות'
    });
  }
  
  // 12. משימות עם הערות רבות
  const tasksWithManyComments = tasks.filter(t => 
    t.comments && t.comments.length > 10
  );
  
  if (tasksWithManyComments.length > 0) {
    insights.push({
      title: `${tasksWithManyComments.length} משימות עם דיונים ארוכים`,
      description: 'משימות אלו כוללות יותר מ-10 הערות. ייתכן שיש בעיה בהגדרה.',
      severity: 'info',
      icon: <MessageSquare className="w-5 h-5" />,
      action: 'בדוק הגדרות'
    });
  }
  
  return insights;
}

const analyticsTab = {
  id: 'analytics',
  label: 'ניתוחים וסטטיסטיקות',
  icon: TrendingUp,
  badge: '',
  content: (
    <div className="bg-white rounded-lg shadow relative">
      <div className="p-6 border-b">
        <h2 className="text-lg font-semibold">ניתוחים וסטטיסטיקות מתקדמות</h2>
        <p className="text-sm text-gray-600 mt-1">מבט על לביצועי הארגון</p>
      </div>
      
      {/* KPIs Section */}
      <div className="p-6">
        <h3 className="text-lg font-medium mb-4">מדדים מרכזיים</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">סה"כ משימות</p>
                <p className="text-3xl font-bold">{tasks.length}</p>
                <p className="text-sm text-blue-100 mt-1">
                  {tasks.filter(t => t.status === 'הושלם').length} הושלמו
                </p>
              </div>
              <CheckSquare className="w-10 h-10 text-blue-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">אחוז השלמה</p>
                <p className="text-3xl font-bold">
                  {Math.round((tasks.filter(t => t.status === 'הושלם').length / (tasks.length || 1)) * 100)}%
                </p>
                <p className="text-sm text-green-100 mt-1">מכלל המשימות</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100">משימות בסיכון</p>
                <p className="text-3xl font-bold">{atRiskTasks.length}</p>
                <p className="text-sm text-yellow-100 mt-1">דורשות התייחסות</p>
              </div>
              <AlertIcon className="w-10 h-10 text-yellow-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100">פרויקטים פעילים</p>
                <p className="text-3xl font-bold">
                  {projects.filter(p => getProjectStatus(p) !== 'פרויקט הושלם').length}
                </p>
                <p className="text-sm text-purple-100 mt-1">מתוך {projects.length}</p>
              </div>
              <FolderOpen className="w-10 h-10 text-purple-200" />
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Pie Chart - Tasks by Department */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium mb-4">התפלגות משימות לפי מחלקות</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={calculateChartData().tasksByDepartment}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={entry => `${entry.name}: ${entry.value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {calculateChartData().tasksByDepartment.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index % 5]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart - Department Performance */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium mb-4">השוואת ביצועים בין מחלקות</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={calculateChartData().departmentPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="performance" fill="#10B981" name="אחוז השלמה" />
                <Bar dataKey="tasks" fill="#3B82F6" name="משימות" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Line Chart - 30 Days Trend */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium mb-4">מגמות - 30 יום אחרונים</h4>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={calculateChartData().last30Days}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="tasks" stroke="#3B82F6" name="משימות חדשות" />
                <Line type="monotone" dataKey="completed" stroke="#10B981" name="משימות שהושלמו" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Radar Chart - Department Metrics */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium mb-4">ביצועי מחלקות - מבט רב מימדי</h4>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={calculateChartData().radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="department" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar name="משימות" dataKey="משימות" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} />
                <Radar name="השלמה%" dataKey="השלמה" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>



{/* Tables Section */}
<h3 className="text-xl font-bold mb-6 mt-12 text-gray-800 flex items-center gap-3">
  <div className="w-1 h-8 bg-gradient-to-b from-blue-600 to-purple-600 rounded-full"></div>
  טבלאות מפורטות
</h3>
<div className="space-y-8">
  
  {/* Tasks Table - עיצוב חדש! */}
  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl overflow-hidden">
<div className="bg-gradient-to-r from-purple-600 to-purple-700 p-3 flex justify-between items-center">    <div className="flex items-center gap-3">
        <CheckSquare className="w-6 h-6 text-white" />
        <h3 className="text-xl font-semibold text-white">טבלת משימות</h3>
        <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm text-white">
          {tasks.length} משימות
        </span>
      </div>
      <button
        onClick={() => exportToCSV(getTableData().tasksTable, 'tasks')}
        className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all flex items-center gap-2 backdrop-blur"
      >
        <Download size={16} />
        ייצא CSV
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-200">
            <th className="p-4 text-right font-semibold text-gray-700">משימה</th>
            <th className="p-4 text-right font-semibold text-gray-700">מוקצה ל</th>
            <th className="p-4 text-right font-semibold text-gray-700">פרויקט</th>
            <th className="p-4 text-right font-semibold text-gray-700">מחלקה</th>
            <th className="p-4 text-center font-semibold text-gray-700">סטטוס</th>
            <th className="p-4 text-center font-semibold text-gray-700">עדיפות</th>
            <th className="p-4 text-center font-semibold text-gray-700">תאריך יעד</th>
          </tr>
        </thead>
        <tbody>
          {getTableData().tasksTable.slice(0, 10).map((task, idx) => (
            <tr key={idx} className="hover:bg-purple-50 transition-colors border-b border-gray-100">
              <td className="p-4 font-medium text-gray-800">{task.title}</td>
              <td className="p-4 text-gray-600">{task.assignee}</td>
              <td className="p-4 text-gray-600">{task.project}</td>
              <td className="p-4 text-gray-600">{task.department}</td>
              <td className="p-4 text-center">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                  task.status === 'הושלם' ? 'bg-green-100 text-green-800' :
                  task.status === 'במסלול' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {task.status}
                </span>
              </td>
              <td className="p-4 text-center">
                <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium ${
                  task.priority === 'important_urgent' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg' :
                  task.priority === 'important_not_urgent' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow' :
                  task.priority === 'not_important_urgent' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white' :
                  'bg-gray-200 text-gray-700'
                }`}>
                  {task.priority === 'important_urgent' ? 'חשוב ודחוף' :
                   task.priority === 'important_not_urgent' ? 'חשוב לא דחוף' :
                   task.priority === 'not_important_urgent' ? 'דחוף לא חשוב' :
                   'לא חשוב/דחוף'}
                </div>
              </td>
              <td className="p-4 text-center">
                {task.isOverdue ? (
                  <span className="flex items-center justify-center gap-1 text-red-600 font-bold">
                    <AlertCircle className="w-4 h-4" />
                    {task.dueDate}
                  </span>
                ) : (
                  <span className="text-gray-600">{task.dueDate}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>

  {/* Projects Table - עיצוב חדש! */}
  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl overflow-hidden">
<div className="bg-gradient-to-r from-green-600 to-green-700 p-3 flex justify-between items-center">      <div className="flex items-center gap-3">
        <FolderOpen className="w-6 h-6 text-white" />
        <h3 className="text-xl font-semibold text-white">טבלת פרויקטים</h3>
        <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm text-white">
          {projects.length} פרויקטים
        </span>
      </div>
      <button
        onClick={() => exportToCSV(getTableData().projectsTable, 'projects')}
        className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all flex items-center gap-2 backdrop-blur"
      >
        <Download size={16} />
        ייצא CSV
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-200">
            <th className="p-4 text-right font-semibold text-gray-700">שם הפרויקט</th>
            <th className="p-4 text-right font-semibold text-gray-700">מחלקה</th>
            <th className="p-4 text-center font-semibold text-gray-700">סטטוס</th>
            <th className="p-4 text-center font-semibold text-gray-700">התקדמות</th>
            <th className="p-4 text-center font-semibold text-gray-700">תאריך יעד</th>
            <th className="p-4 text-center font-semibold text-gray-700">משימות</th>
            <th className="p-4 text-center font-semibold text-gray-700">צוות</th>
          </tr>
        </thead>
        <tbody>
          {getTableData().projectsTable.map((project, idx) => (
            <tr key={idx} className={`hover:bg-green-50 transition-colors border-b border-gray-100 ${
              project.isDelayed ? 'bg-red-50 hover:bg-red-100' : ''
            }`}>
              <td className="p-4 font-medium text-gray-800">{project.name}</td>
              <td className="p-4 text-gray-600">{project.department}</td>
              <td className="p-4 text-center">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                  project.status === 'פרויקט הושלם' ? 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow' :
                  project.status === 'פרויקט בתהליך' ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow' :
                  'bg-gray-200 text-gray-700'
                }`}>
                  {project.status}
                </span>
              </td>
              <td className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                      <div 
                        className={`h-3 rounded-full transition-all duration-500 shadow-sm ${
                          project.progress >= 80 ? 'bg-gradient-to-r from-green-400 to-green-500' :
                          project.progress >= 50 ? 'bg-gradient-to-r from-blue-400 to-blue-500' :
                          project.progress >= 20 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' :
                          'bg-gradient-to-r from-red-400 to-red-500'
                        }`} 
                        style={{width: `${project.progress}%`}}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 min-w-[45px] text-center">
                    {project.progress}%
                  </span>
                </div>
              </td>
              <td className="p-4 text-center">
                {project.isDelayed ? (
                  <span className="flex items-center justify-center gap-1 text-red-600 font-bold">
                    <AlertTriangle className="w-4 h-4" />
                    {project.dueDate}
                  </span>
                ) : (
                  <span className="text-gray-600">{project.dueDate}</span>
                )}
              </td>
              <td className="p-4 text-center">
                <span className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium text-gray-700">
                  {project.tasks}
                </span>
              </td>
              <td className="p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{project.team}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>

  {/* Departments Table - עיצוב חדש! */}
  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl overflow-hidden">
<div className="bg-gradient-to-r from-blue-600 to-blue-700 p-3 flex justify-between items-center">      <div className="flex items-center gap-3">
        <Building2 className="w-6 h-6 text-white" />
        <h3 className="text-xl font-semibold text-white">טבלת מחלקות</h3>
        <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm text-white">
          {departments.length} מחלקות
        </span>
      </div>
      <button
        onClick={() => exportToCSV(getTableData().departmentsTable, 'departments')}
        className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all flex items-center gap-2 backdrop-blur"
      >
        <Download size={16} />
        ייצא CSV
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-200">
            <th className="p-4 text-right font-semibold text-gray-700">מחלקה</th>
            <th className="p-4 text-right font-semibold text-gray-700">מנהל</th>
            <th className="p-4 text-center font-semibold text-gray-700">עובדים</th>
            <th className="p-4 text-center font-semibold text-gray-700">פרויקטים</th>
            <th className="p-4 text-center font-semibold text-gray-700">משימות</th>
            <th className="p-4 text-center font-semibold text-gray-700">% השלמה</th>
          </tr>
        </thead>
        <tbody>
          {getTableData().departmentsTable.map((dept, idx) => (
            <tr key={idx} className="hover:bg-blue-50 transition-colors border-b border-gray-100">
              <td className="p-4 font-medium text-gray-800">{dept.name}</td>
              <td className="p-4 text-gray-600 flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                {dept.manager}
              </td>
              <td className="p-4 text-center">
                <span className="bg-blue-100 px-3 py-1 rounded-full text-sm font-medium text-blue-800">
                  {dept.employees}
                </span>
              </td>
              <td className="p-4 text-center">
                <span className="bg-purple-100 px-3 py-1 rounded-full text-sm font-medium text-purple-800">
                  {dept.projects}
                </span>
              </td>
              <td className="p-4 text-center">
                <span className="bg-indigo-100 px-3 py-1 rounded-full text-sm font-medium text-indigo-800">
                  {dept.tasks}
                </span>
              </td>
              <td className="p-4 text-center">
                <div className="flex items-center justify-center">
                  <div className={`px-4 py-2 rounded-lg font-semibold text-sm shadow-sm ${
                    dept.completion >= 75 ? 'bg-gradient-to-r from-green-400 to-green-500 text-white' :
                    dept.completion >= 50 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white' :
                    'bg-gradient-to-r from-red-400 to-red-500 text-white'
                  }`}>
                    {dept.completion}%
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>

  {/* Employees Table - עיצוב חדש! */}
  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl overflow-hidden">
<div className="bg-gradient-to-r from-orange-600 to-orange-700 p-3 flex justify-between items-center">      <div className="flex items-center gap-3">
        <Users className="w-6 h-6 text-white" />
        <h3 className="text-xl font-semibold text-white">טבלת עובדים</h3>
        <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm text-white">
          {users.length} עובדים
        </span>
      </div>
      <button
        onClick={() => exportToCSV(getTableData().employeesTable, 'employees')}
        className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all flex items-center gap-2 backdrop-blur"
      >
        <Download size={16} />
        ייצא CSV
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-200">
            <th className="p-4 text-right font-semibold text-gray-700">שם</th>
            <th className="p-4 text-right font-semibold text-gray-700">מחלקה</th>
            <th className="p-4 text-right font-semibold text-gray-700">תפקיד</th>
            <th className="p-4 text-center font-semibold text-gray-700">משימות פעילות</th>
            <th className="p-4 text-center font-semibold text-gray-700">שגרות יומיות</th>
            <th className="p-4 text-center font-semibold text-gray-700">ביצועים</th>
          </tr>
        </thead>
        <tbody>
          {getTableData().employeesTable.slice(0, 10).map((emp, idx) => (
            <tr key={idx} className="hover:bg-orange-50 transition-colors border-b border-gray-100">
              <td className="p-4 font-medium text-gray-800">{emp.name}</td>
              <td className="p-4 text-gray-600">{emp.department}</td>
              <td className="p-4 text-gray-600">{emp.role}</td>
              <td className="p-4 text-center">
                <span className="bg-orange-100 px-3 py-1 rounded-full text-sm font-medium text-orange-800">
                  {emp.activeTasks}
                </span>
              </td>
              <td className="p-4 text-center">
                <span className="bg-yellow-100 px-3 py-1 rounded-full text-sm font-medium text-yellow-800">
                  {emp.dailyRoutines}
                </span>
              </td>
              <td className="p-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  {emp.performance >= 90 && (
                    <div className="animate-pulse">
                      <Award className="w-5 h-5 text-yellow-500" />
                    </div>
                  )}
                  <span className={`font-semibold ${
                    emp.performance >= 90 ? 'text-green-600' :
                    emp.performance >= 70 ? 'text-blue-600' :
                    emp.performance >= 50 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {emp.performance}%
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>

  {/* Daily Routines Table - עיצוב חדש! */}
  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl overflow-hidden">
<div className="bg-gradient-to-r from-indigo-600 to-indigo-700 p-3 flex justify-between items-center">      <div className="flex items-center gap-3">
        <RotateCcw className="w-6 h-6 text-white" />
        <h3 className="text-xl font-semibold text-white">טבלת שגרות יומיות</h3>
        <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm text-white">
          {dailyRoutines.length} שגרות
        </span>
      </div>
      <button
        onClick={() => exportToCSV(getTableData().routinesTable, 'routines')}
        className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all flex items-center gap-2 backdrop-blur"
      >
        <Download size={16} />
        ייצא CSV
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-200">
            <th className="p-4 text-right font-semibold text-gray-700">שגרה</th>
            <th className="p-4 text-right font-semibold text-gray-700">תדירות</th>
            <th className="p-4 text-right font-semibold text-gray-700">מבצעים</th>
            <th className="p-4 text-center font-semibold text-gray-700">ביצוע היום</th>
            <th className="p-4 text-center font-semibold text-gray-700">% יומי</th>
            <th className="p-4 text-center font-semibold text-gray-700">% שבועי</th>
            <th className="p-4 text-center font-semibold text-gray-700">% חודשי</th>
          </tr>
        </thead>
        <tbody>
          {getTableData().routinesTable.map((routine, idx) => (
            <tr key={idx} className="hover:bg-indigo-50 transition-colors border-b border-gray-100">
              <td className="p-4 font-medium text-gray-800">{routine.name}</td>
              <td className="p-4">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                  routine.frequency === 'יומי' 
                    ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow' 
                    : 'bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow'
                }`}>
                  {routine.frequency}
                </span>
              </td>
              <td className="p-4 text-gray-600">{routine.assignees}</td>
              <td className="p-4 text-center">
                <span className="font-medium text-gray-700">{routine.completedToday}</span>
              </td>
              <td className="p-4 text-center">
                <div className={`inline-flex items-center px-3 py-1.5 rounded-lg font-medium text-sm ${
                  routine.dailyRate >= 80 ? 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow' :
                  routine.dailyRate >= 50 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow' :
                  'bg-gradient-to-r from-red-400 to-red-500 text-white shadow'
                }`}>
                  {routine.dailyRate}%
                </div>
              </td>
              <td className="p-4 text-center">
                <span className={`font-medium ${
                  routine.weeklyRate >= 80 ? 'text-green-600' :
                  routine.weeklyRate >= 50 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {routine.weeklyRate}%
                </span>
              </td>
              <td className="p-4 text-center">
                <span className={`font-medium ${
                  routine.monthlyRate >= 80 ? 'text-green-600' :
                  routine.monthlyRate >= 50 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {routine.monthlyRate}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>

{/* Top Performers - עם נתונים אמיתיים לכל התקופות */}
<div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl shadow-2xl p-1">
  <div className="bg-white rounded-xl p-8">
    <h3 className="text-2xl font-bold mb-6 flex items-center gap-3 text-gray-800">
      <div className="p-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-xl shadow-lg">
        <Award className="w-8 h-8 text-white" />
      </div>
      לוח מצטיינים
    </h3>

    {/* כפתורי בחירת תקופה */}
    <div className="mb-6 flex gap-2 bg-gray-100 p-1 rounded-lg">
      <button
        onClick={() => setTopPerformersView('week')}
        className={`px-4 py-2 rounded-md font-medium transition-all ${
          topPerformersView === 'week' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        שבוע
      </button>
      <button
        onClick={() => setTopPerformersView('month')}
        className={`px-4 py-2 rounded-md font-medium transition-all ${
          topPerformersView === 'month' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        חודש
      </button>
      <button
        onClick={() => setTopPerformersView('quarter')}
        className={`px-4 py-2 rounded-md font-medium transition-all ${
          topPerformersView === 'quarter' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        רבעון
      </button>
      <button
        onClick={() => setTopPerformersView('year')}
        className={`px-4 py-2 rounded-md font-medium transition-all ${
          topPerformersView === 'year' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        שנה
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-lg text-gray-700">
            עובד {topPerformersView === 'week' ? 'השבוע' : 
                  topPerformersView === 'month' ? 'החודש' : 
                  topPerformersView === 'quarter' ? 'הרבעון' : 'השנה'}
          </h4>
          <div className="p-2 bg-yellow-400 rounded-lg">
            <User className="w-5 h-5 text-white" />
          </div>
        </div>
        {(() => {
          // חישוב מצטיין לפי התקופה שנבחרה
          const now = new Date();
          let startDate;
          
          switch(topPerformersView) {
            case 'week':
              startDate = new Date();
              startDate.setDate(startDate.getDate() - 7);
              break;
            case 'month':
              startDate = new Date();
              startDate.setDate(1);
              break;
            case 'quarter':
              startDate = new Date();
              const currentQuarter = Math.floor(startDate.getMonth() / 3);
              startDate.setMonth(currentQuarter * 3, 1);
              break;
            case 'year':
              startDate = new Date(now.getFullYear(), 0, 1);
              break;
          }
          
          const topEmployee = users.map(user => {
            const userCompletedTasks = tasks.filter(t => 
              (t.assigneeId === user.id || t.assigneeIds?.includes(user.id)) &&
              t.status === 'הושלם' &&
              t.completedAt && 
              new Date(t.completedAt) >= startDate
            ).length;
            
            return { user, completedTasks: userCompletedTasks };
          }).sort((a, b) => b.completedTasks - a.completedTasks)[0];
          
          const daysInPeriod = topPerformersView === 'week' ? 7 : 
                              topPerformersView === 'month' ? 30 :
                              topPerformersView === 'quarter' ? 90 : 365;
          
          return (
            <>
              <p className="text-2xl font-bold text-gray-800 mb-2">
                {topEmployee?.user.name || 'טרם נבחר'}
              </p>
              <div className="space-y-1">
                <p className="text-sm text-gray-600">🏆 השלים {topEmployee?.completedTasks || 0} משימות</p>
                <p className="text-sm text-gray-600">⚡ {getTableData().employeesTable.find(e => e.name === topEmployee?.user.name)?.performance || 0}% אחוז ביצועים</p>
                <p className="text-sm text-gray-600">📅 ממוצע יומי: {topEmployee?.completedTasks ? 
                  Math.round(topEmployee.completedTasks / daysInPeriod) : 0} משימות</p>
              </div>
            </>
          );
        })()}
      </div>
      
      <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-lg text-gray-700">
            מחלקת {topPerformersView === 'week' ? 'השבוע' : 
                    topPerformersView === 'month' ? 'החודש' : 
                    topPerformersView === 'quarter' ? 'הרבעון' : 'השנה'}
          </h4>
          <div className="p-2 bg-orange-400 rounded-lg">
            <Building2 className="w-5 h-5 text-white" />
          </div>
        </div>
        {(() => {
          // חישוב מחלקה מצטיינת לפי התקופה
          const now = new Date();
          let startDate;
          
          switch(topPerformersView) {
            case 'week':
              startDate = new Date();
              startDate.setDate(startDate.getDate() - 7);
              break;
            case 'month':
              startDate = new Date();
              startDate.setDate(1);
              break;
            case 'quarter':
              startDate = new Date();
              const currentQuarter = Math.floor(startDate.getMonth() / 3);
              startDate.setMonth(currentQuarter * 3, 1);
              break;
            case 'year':
              startDate = new Date(now.getFullYear(), 0, 1);
              break;
          }
          
          const departmentStats = departments.map(dept => {
            const deptTasks = tasks.filter(t => 
              t.departmentId === dept.id &&
              t.completedAt &&
              new Date(t.completedAt) >= startDate
            );
            
            const completedTasks = deptTasks.filter(t => t.status === 'הושלם').length;
            const totalTasks = deptTasks.length;
            const completion = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
            
            return { dept, completion, totalTasks, completedTasks };
          }).sort((a, b) => b.completion - a.completion)[0];
          
          const daysInPeriod = topPerformersView === 'week' ? 7 : 
                              topPerformersView === 'month' ? 30 :
                              topPerformersView === 'quarter' ? 90 : 365;
          
          return (
            <>
              <p className="text-2xl font-bold text-gray-800 mb-2">
                {departmentStats?.dept.name || 'טרם נבחרה'}
              </p>
              <div className="space-y-1">
                <p className="text-sm text-gray-600">🎯 {departmentStats?.completion || 0}% אחוז השלמת משימות</p>
                <p className="text-sm text-gray-600">📈 {departmentStats?.completedTasks || 0}/{departmentStats?.totalTasks || 0} משימות הושלמו</p>
                <p className="text-sm text-gray-600">👥 {departmentStats?.dept.employees?.length || getTableData().departmentsTable.find(d => d.name === departmentStats?.dept.name)?.employees || 0} עובדים</p>
                <p className="text-sm text-gray-600">📊 ממוצע יומי: {departmentStats?.completedTasks ? 
                  (departmentStats.completedTasks / daysInPeriod).toFixed(1) : 0} משימות</p>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  </div>
</div>
</div>
          </div>
        

      {/* Floating Insights Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => {
            // Generate insights
            const insights = generateSmartInsights();
            
            // Create modal content
 // Create and show modal
// Create and show modal
const modal = document.createElement('div');
modal.style.cssText = 'position: fixed; inset: 0; z-index: 50; display: flex; align-items: center; justify-content: center; padding: 1rem; background: rgba(0,0,0,0.5);';

// Click outside to close
modal.onclick = function(e) {
  if (e.target === modal) {
    modal.remove();
  }
};

const content = document.createElement('div');
content.style.cssText = 'background: white; border-radius: 1rem; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); max-width: 56rem; width: 100%; max-height: 90vh; overflow: hidden;';

content.innerHTML = `
  <div style="background: linear-gradient(to right, #7c3aed, #6366f1); padding: 1.5rem; color: white;">
    <div style="display: flex; align-items: center; justify-content: space-between;">
      <div style="display: flex; align-items: center; gap: 0.75rem;">
        <svg style="width: 2rem; height: 2rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
        </svg>
        <h2 style="font-size: 1.5rem; font-weight: bold;">תובנות חכמות</h2>
      </div>
      <button onclick="document.getElementById('insights-modal').remove()" style="padding: 0.5rem; background: rgba(255,255,255,0.2); border-radius: 0.5rem; border: none; cursor: pointer;">
        <svg style="width: 1.5rem; height: 1.5rem; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  </div>
  <div style="padding: 1.5rem; overflow-y: auto; max-height: calc(90vh - 120px);">
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1rem;">
      ${insights.map(insight => `
        <div style="padding: 1rem; border-radius: 0.5rem; border: 2px solid ${
          insight.severity === 'critical' ? '#fecaca' :
          insight.severity === 'warning' ? '#fde68a' :
          insight.severity === 'info' ? '#bfdbfe' : '#bbf7d0'
        }; background: ${
          insight.severity === 'critical' ? '#fef2f2' :
          insight.severity === 'warning' ? '#fef3c7' :
          insight.severity === 'info' ? '#eff6ff' : '#f0fdf4'
        };">
          <div style="display: flex; align-items: start; gap: 0.75rem;">
            <div style="padding: 0.5rem; border-radius: 0.5rem; background: ${
              insight.severity === 'critical' ? '#fecaca' :
              insight.severity === 'warning' ? '#fde68a' :
              insight.severity === 'info' ? '#bfdbfe' : '#bbf7d0'
            };">
              ${insight.severity === 'critical' ? '⚠️' :
                insight.severity === 'warning' ? '⚡' :
                insight.severity === 'info' ? 'ℹ️' : '✅'}
            </div>
            <div style="flex: 1;">
              <h4 style="font-weight: 600; color: #1f2937; margin-bottom: 0.25rem;">${insight.title}</h4>
              <p style="font-size: 0.875rem; color: #4b5563; margin-bottom: 0.5rem;">${insight.description}</p>
              ${insight.action ? `
                <button style="font-size: 0.75rem; font-weight: 500; padding: 0.25rem 0.75rem; border-radius: 9999px; border: none; cursor: pointer; background: ${
                  insight.severity === 'critical' ? '#dc2626' :
                  insight.severity === 'warning' ? '#d97706' :
                  insight.severity === 'info' ? '#2563eb' : '#059669'
                }; color: white;">
                  ${insight.action}
                </button>
              ` : ''}
            </div>
          </div>
        </div>
      `).join('')}
    </div>
  </div>
`;

modal.id = 'insights-modal';
modal.appendChild(content);
document.body.appendChild(modal);

          }}
          className="group bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110"
        >
          <Brain className="w-6 h-6" />
          <span className="absolute -top-10 right-0 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            קבל תובנות חכמות
          </span>
        </button>
      </div>
    </div>
  )
};

// Return the analytics tab
return [managementTab, personalTab, analyticsTab];
})();

window.closeInsightsModal = function() {
  const modal = document.getElementById('insights-modal');
  if (modal) modal.remove();
    }
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">לוח בקרה - מנכ"ל</h1>
      <div className="mb-8">
  <TabsComponent tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
</div>

{renderInlineContent()}
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          title="סך הכל פרויקטים" 
          value={filteredDashboardProjects.length} 
          icon={FolderOpen} 
          color="blue"
        />
        <StatCard 
          title="סך הכל משימות" 
          value={filteredDashboardTasks.length} 
          icon={CheckSquare} 
          color="green"
        />
        <StatCard 
          title="לא אושרו" 
          value={unacknowledgedTasks.length} 
          icon={Clock} 
          color="yellow"
        />
        <StatCard 
          title="בסיכון/חסום" 
          value={atRiskTasks.length} 
          icon={AlertTriangle} 
          color="red"
        />
      </div>

      {/* הערות חדשות */}

      {activeTab !== 'analytics' && (
  <div className="mb-6">
    <CombinedCommentsWidget />
  </div>
)}

    {/* Daily Routines Section */}
{activeTab !== 'analytics' && <DailyRoutinesWidget />}

      {/* Day Opening & Summary Section */}
      {activeTab !== 'analytics' && (
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-purple-500 rounded-lg flex items-center justify-center">
              <Sun className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">פתיחה וסיכום יום</h2>
              <p className="text-sm text-gray-600">נהל את היום שלך בצורה מסודרת</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/day-opening')}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2 text-sm font-medium"
            >
              <Sun className="w-4 h-4" />
              פתיחת יום
            </button>
            <button
              onClick={() => navigate('/day-summary')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium"
            >
              <Moon className="w-4 h-4" />
              סיכום יום
            </button>
            <button
              onClick={() => navigate('/team-summaries')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
            >
              <Users className="w-4 h-4" />
              סיכומי הארגון
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sun className="w-5 h-5 text-orange-600" />
              <h3 className="font-semibold text-orange-800">פתיחת יום</h3>
            </div>
            <p className="text-sm text-orange-700">
              תכנן את היום: יעדים, משימות מרכזיות, פגישות וחסמים צפויים
            </p>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Moon className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-800">סיכום יום</h3>
            </div>
            <p className="text-sm text-purple-700">
              סכם את היום: הישגים, אתגרים, תובנות ותכנון למחר
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-800">סיכומי הארגון</h3>
            </div>
            <p className="text-sm text-blue-700">
              צפה בפתיחות וסיכומי יום של כל העובדים בארגון
            </p>
          </div>
        </div>
      </div>
)}

      

      {/* Inline Content Display */}
      {renderInlineContent()}

      {/* Priority Matrix */}
      {activeTab !== 'analytics' && (
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h2 className="text-lg font-semibold mb-4">מטריצת עדיפויות</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {priorityMatrix.map((priority) => (
            <button
              key={priority.key}
              onClick={() => setShowPriorityTasks(priority)}
              className={`${priority.color} text-white p-4 rounded-lg text-center hover:opacity-90 transition-opacity`}
            >
              <div className="text-lg font-semibold">{priority.label}</div>
              <div className="text-2xl font-bold">{priority.tasks.length}</div>
              <div className="text-sm opacity-90">משימות</div>
            </button>
          ))}
        </div>
      </div>
)}
      {/* Modals */}
      {showNewProject && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowNewProject(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowNewProject(false)}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">פרויקט חדש</h3>
                <p className="text-blue-100 text-sm">צור פרויקט חדש עם כל הפרטים הנדרשים</p>
              </div>
              <button
                type="submit"
                form="newProjectForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Plus className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="newProjectForm" className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  {/* Project Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      שם הפרויקט
                    </label>
                    <input 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                      placeholder="הכנס שם הפרויקט" 
                      value={newProject.name} 
                      onChange={(e)=>setNewProject(prev => ({...prev, name: e.target.value}))} 
                      required
                    />
                  </div>

                  {/* Project Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      תיאור הפרויקט
                    </label>
                    <textarea 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all h-24 resize-none" 
                      placeholder="תאר את הפרויקט בקצרה..." 
                      value={newProject.description} 
                      onChange={(e)=>setNewProject(prev => ({...prev, description: e.target.value}))} 
                    />
                  </div>

                  {/* Project Scope */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-500" />
                      היקף הפרויקט
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                      value={newProject.scope} 
                      onChange={(e)=>setNewProject(prev => ({...prev, scope: e.target.value}))}
                    >
                      <option value="personal">אישי</option>
                      <option value="department">מחלקתי</option>
                      <option value="organization">ארגוני</option>
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {/* Department */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      מחלקה
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={newProject.departmentId} 
                      onChange={(e)=>setNewProject(prev => ({...prev, departmentId: e.target.value}))}
                    >
                      <option value="">בחר מחלקה</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      תאריך התחלה
                    </label>
                    <input 
                      type="date" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                      value={newProject.startDate} 
                      onChange={(e)=>setNewProject(prev => ({...prev, startDate: e.target.value}))} 
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      תאריך סיום
                    </label>
                    <input 
                      type="date" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                      value={newProject.endDate} 
                      onChange={(e)=>setNewProject({...newProject,endDate:e.target.value})} 
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" 
                  onClick={()=>setShowNewProject(false)}
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" 
                  onClick={async(e)=>{ 
                    e.preventDefault();
                    const projectId = await createProject(newProject); 
                    setShowNewProject(false); 
                    setNewProject({ name: '', description: '', scope: 'department', departmentId: '', startDate: '', endDate: '' });
                    if (projectId) {
                      setShowProjectTasks(projectId);
                    }
                  }}
                >
                  צור פרויקט
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Tasks for New Project Modal */}
      {showProjectTasks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowProjectTasks(null)}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">צור משימות לפרויקט החדש</h3>
                <p className="text-blue-100 text-sm">הוסף משימות לפרויקט החדש שנוצר</p>
              </div>
              <div className="w-8 h-8"></div>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              {newProjectTasks.map((task, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input 
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                      placeholder="כותרת משימה" 
                      value={task.title} 
                      onChange={(e) => {
                        const updatedTasks = [...newProjectTasks];
                        updatedTasks[index].title = e.target.value;
                        setNewProjectTasks(updatedTasks);
                      }}
                    />
                    <select 
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={task.priority}
                      onChange={(e) => {
                        const updatedTasks = [...newProjectTasks];
                        updatedTasks[index].priority = e.target.value;
                        setNewProjectTasks(updatedTasks);
                      }}
                    >
                      <option value="important_urgent">חשוב ודחוף</option>
                      <option value="important_not_urgent">חשוב ולא דחוף</option>
                      <option value="not_important_urgent">דחוף ולא חשוב</option>
                      <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                    </select>
                  </div>
                  <textarea 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg mt-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                    placeholder="תיאור משימה" 
                    value={task.description} 
                    onChange={(e) => {
                      const updatedTasks = [...newProjectTasks];
                      updatedTasks[index].description = e.target.value;
                      setNewProjectTasks(updatedTasks);
                    }}
                  />
                  <div className="flex justify-between items-center mt-3">
                    <input 
                      type="date" 
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                      value={task.dueDate} 
                      onChange={(e) => {
                        const updatedTasks = [...newProjectTasks];
                        updatedTasks[index].dueDate = e.target.value;
                        setNewProjectTasks(updatedTasks);
                      }}
                    />
                    <button 
                      className="px-3 py-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      onClick={() => {
                        const updatedTasks = newProjectTasks.filter((_, i) => i !== index);
                        setNewProjectTasks(updatedTasks);
                      }}
                    >
                      מחק
                    </button>
                  </div>
                </div>
              ))}
              
              <button 
                className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors"
                onClick={() => setNewProjectTasks([...newProjectTasks, { title: '', description: '', priority: 'important_not_urgent', dueDate: '', estimate: 0 }])}
              >
                + הוסף משימה
              </button>
            </div>
            
            {/* Action Buttons */}
            <div className="px-6 pb-6 flex justify-end gap-3">
              <button 
                className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                onClick={() => setShowProjectTasks(null)}
              >
                דלג
              </button>
              <button 
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                onClick={async () => {
                  const validTasks = newProjectTasks.filter(t => t.title.trim() && t.description.trim());
                  for (const task of validTasks) {
                    await createTask({
                      ...task,
                      projectId: showProjectTasks,
                      scope: 'project'
                    });
                  }
                  setNewProjectTasks([]);
                  setShowProjectTasks(null);
                }}
              >
                צור {newProjectTasks.filter(t => t.title.trim() && t.description.trim()).length} משימות
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewTask && (
        <SimpleTaskForm
          isOpen={showNewTask}
          onClose={() => setShowNewTask(false)}
          onSubmit={async (taskData) => {
            await createTask(taskData);
            setShowNewTask(false);
          }}
          currentUser={currentUser}
          users={users}
          projects={projects}
        />
      )}

      {showNewDepartment && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowNewDepartment(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-xl">
                  <Building2 size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">מחלקה חדשה</h3>
                  <p className="text-green-100 text-sm">צור מחלקה חדשה עם כל הפרטים הנדרשים</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-5">
                  {/* Department Name */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <Building2 size={16} className="text-green-600" />
                      שם המחלקה
                    </label>
                    <input 
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-gray-50 hover:bg-white shadow-sm" 
                      placeholder="הכנס שם המחלקה" 
                      value={newDepartment.name} 
                      onChange={(e)=>setNewDepartment({...newDepartment,name:e.target.value})} 
                      required
                    />
                  </div>

                  {/* Department Description */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <FileText size={16} className="text-green-600" />
                      תיאור המחלקה
                    </label>
                    <textarea 
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-gray-50 hover:bg-white shadow-sm h-24 resize-none" 
                      placeholder="תאר את המחלקה בקצרה..." 
                      value={newDepartment.description} 
                      onChange={(e)=>setNewDepartment({...newDepartment,description:e.target.value})} 
                    />
                  </div>
                </div>
                
                {/* Right Column */}
                <div className="space-y-5">
                  {/* Department Manager */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <User size={16} className="text-green-600" />
                      מנהל המחלקה
                    </label>
                    <select 
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-gray-50 hover:bg-white shadow-sm"
                      value={newDepartment.managerId}
                      onChange={(e)=>setNewDepartment({...newDepartment,managerId:e.target.value})}
                    >
                      <option value="">בחר מנהל מחלקה</option>
                      {users.filter(user => user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager').map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Activity Status */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <Activity size={16} className="text-green-600" />
                      סטטוס פעילות
                    </label>
                    <select 
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-gray-50 hover:bg-white shadow-sm"
                      value={newDepartment.isActive}
                      onChange={(e)=>setNewDepartment({...newDepartment,isActive:e.target.value === 'true'})}
                    >
                      <option value={true}>פעילה</option>
                      <option value={false}>לא פעילה</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button 
                  className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all font-medium" 
                  onClick={()=>setShowNewDepartment(false)}
                >
                  ביטול
                </button>
                <button 
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all font-medium shadow-lg hover:shadow-xl" 
                  onClick={async()=>{ 
                    await createDepartment(newDepartment); 
                    setShowNewDepartment(false); 
                    setNewDepartment({ name: '', description: '', managerId: '', isActive: true }); 
                  }}
                >
                  צור מחלקה
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Priority Tasks Modal */}
      {showPriorityTasks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-green-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowPriorityTasks(null)}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">משימות - {showPriorityTasks.label}</h3>
                <p className="text-green-100 text-sm">צפה במשימות לפי עדיפות</p>
              </div>
              <div className="w-8 h-8"></div>
            </div>

            {/* Content */}
            <div className="p-6">
              {showPriorityTasks.tasks.length === 0 ? (
                <p className="text-gray-500 text-center py-8">אין משימות בעדיפות זו</p>
              ) : (
                <div className="space-y-3">
                  {showPriorityTasks.tasks.map((task) => (
                    <div key={task.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => { setShowPriorityTasks(null); navigate(`/tasks/${task.id}`); }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{task.title}</h4>
                          <p className="text-sm text-gray-600">{task.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`px-2 py-1 rounded text-xs ${showPriorityTasks.color} text-white`}>
                              {showPriorityTasks.label}
                            </span>
                            {task.dueDate && (
                              <span className="text-xs text-gray-500">דדליין: {new Date(task.dueDate).toLocaleDateString('he-IL')}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">
                            {task.assigneeIds && task.assigneeIds.length > 0 ? (
                              <div>מוקצים: {task.assigneeIds.length}</div>
                            ) : task.assigneeId ? (
                              <div>מוקצה: {users.find(u => u.id === task.assigneeId)?.name || 'לא ידוע'}</div>
                            ) : (
                              <div>לא מוקצה</div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500">
                              {task.projectId && (
                                <div>פרויקט: {projects.find(p => p.id === task.projectId)?.name || 'לא ידוע'}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal for Org Admin */}
      {showEditProject && editProject && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditProject(false);
            setEditProject(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditProject(false);
                  setEditProject(null);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך פרויקט</h3>
                <p className="text-blue-100 text-sm">ערוך את פרטי הפרויקט</p>
              </div>
              <button
                type="submit"
                form="editProjectForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editProjectForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateProject(editProject.id, editProject);
                setShowEditProject(false);
                setEditProject(null);
              } catch (error) {
                console.error('Error updating project:', error);
              }
            }} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      שם הפרויקט
                    </label>
                    <input 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                      placeholder="שם הפרויקט" 
                      value={editProject.name} 
                      onChange={(e) => setEditProject({...editProject, name: e.target.value})} 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      תיאור הפרויקט
                    </label>
                    <textarea 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all h-24 resize-none" 
                      placeholder="תיאור הפרויקט" 
                      value={editProject.description} 
                      onChange={(e) => setEditProject({...editProject, description: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-500" />
                      תחום
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={editProject.scope}
                      onChange={(e) => setEditProject({...editProject, scope: e.target.value})}
                    >
                      <option value="personal">אישי</option>
                      <option value="department">מחלקה</option>
                      <option value="organization">ארגון</option>
                    </select>
                  </div>
                </div>
                
                {/* Right Column - Additional Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      בחר מחלקה
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={editProject.departmentId}
                      onChange={(e) => setEditProject({...editProject, departmentId: e.target.value})}
                    >
                      <option value="">בחר מחלקה</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        תאריך התחלה
                      </label>
                      <input 
                        type="date" 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                        placeholder="תאריך התחלה" 
                        value={editProject.startDate} 
                        onChange={(e) => setEditProject({...editProject, startDate: e.target.value})} 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        תאריך סיום
                      </label>
                      <input 
                        type="date" 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                        placeholder="תאריך סיום" 
                        value={editProject.endDate} 
                        onChange={(e) => setEditProject({...editProject, endDate: e.target.value})} 
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowEditProject(false);
                    setEditProject(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Task Modal for Org Admin */}
      {showEditTask && editTask && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditTask(false);
            setEditTask(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-purple-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditTask(false);
                  setEditTask(null);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך משימה</h3>
                <p className="text-purple-100 text-sm">ערוך את פרטי המשימה</p>
              </div>
              <button
                type="submit"
                form="editTaskForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editTaskForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateTask(editTask.id, editTask);
                setShowEditTask(false);
                setEditTask(null);
              } catch (error) {
                console.error('Error updating task:', error);
              }
            }} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      כותרת המשימה
                    </label>
                    <input 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                      placeholder="כותרת המשימה" 
                      value={editTask.title} 
                      onChange={(e) => setEditTask({...editTask, title: e.target.value})} 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      תיאור המשימה
                    </label>
                    <textarea 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all h-24 resize-none" 
                      placeholder="תיאור המשימה" 
                      value={editTask.description} 
                      onChange={(e) => setEditTask({...editTask, description: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-gray-500" />
                      עדיפות
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                      value={editTask.priority}
                      onChange={(e) => setEditTask({...editTask, priority: e.target.value})}
                    >
                      <option value="important_urgent">חשוב ודחוף</option>
                      <option value="important_not_urgent">חשוב ולא דחוף</option>
                      <option value="not_important_urgent">דחוף ולא חשוב</option>
                      <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                    </select>
                  </div>
                </div>
                
                {/* Right Column - Additional Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      דדליין
                    </label>
                    <input 
                      type="date" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                      placeholder="דדליין" 
                      value={editTask.dueDate} 
                      onChange={(e) => setEditTask({...editTask, dueDate: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      הערכת שעות
                    </label>
                    <input 
                      type="number" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                      placeholder="הערכת שעות" 
                      value={editTask.estimate} 
                      onChange={(e) => setEditTask({...editTask, estimate: parseInt(e.target.value) || 0})} 
                    />
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowEditTask(false);
                    setEditTask(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Team Modal */}
      {showNewTeam && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowNewTeam(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-orange-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowNewTeam(false)}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">צור צוות חדש</h3>
                <p className="text-orange-100 text-sm">הוסף צוות חדש למערכת</p>
              </div>
              <button
                type="submit"
                form="newTeamForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Plus className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="newTeamForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createTeam(newTeam);
                setShowNewTeam(false);
                setNewTeam({
                  name: '',
                  description: '',
                  departmentId: '',
                  managerId: '',
                  memberIds: [],
                  isActive: true
                });
              } catch (error) {
                console.error('Error creating team:', error);
              }
            }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">שם הצוות</label>
                  <input 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" 
                    placeholder="שם הצוות" 
                    value={newTeam.name} 
                    onChange={(e) => setNewTeam({...newTeam, name: e.target.value})} 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">תיאור</label>
                  <textarea 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 h-24 resize-none" 
                    placeholder="תיאור הצוות" 
                    value={newTeam.description} 
                    onChange={(e) => setNewTeam({...newTeam, description: e.target.value})} 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">מחלקה</label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      value={newTeam.departmentId}
                      onChange={(e) => setNewTeam({...newTeam, departmentId: e.target.value})}
                    >
                      <option value="">בחר מחלקה</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">מנהל צוות</label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      value={newTeam.managerId}
                      onChange={(e) => setNewTeam({...newTeam, managerId: e.target.value})}
                    >
                      <option value="">בחר מנהל</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">חברי צוות</label>
                  <select 
                    multiple
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 h-32"
                    value={newTeam.memberIds}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setNewTeam({...newTeam, memberIds: selectedOptions});
                    }}
                  >
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">החזק Ctrl (או Cmd) לבחירה מרובה</p>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="teamActive"
                    checked={newTeam.isActive}
                    onChange={(e) => setNewTeam({...newTeam, isActive: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="teamActive" className="text-sm text-gray-700">צוות פעיל</label>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => setShowNewTeam(false)}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  צור צוות
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditTeam && editTeam && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditTeam(false);
            setEditTeam(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditTeam(false);
                  setEditTeam(null);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך צוות</h3>
                <p className="text-blue-100 text-sm">ערוך את פרטי הצוות</p>
              </div>
              <button
                type="submit"
                form="editTeamForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editTeamForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateTeam(editTeam.id, editTeam);
                setShowEditTeam(false);
                setEditTeam(null);
              } catch (error) {
                console.error('Error updating team:', error);
              }
            }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">שם הצוות</label>
                  <input 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                    placeholder="שם הצוות" 
                    value={editTeam.name} 
                    onChange={(e) => setEditTeam({...editTeam, name: e.target.value})} 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">תיאור</label>
                  <textarea 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24 resize-none" 
                    placeholder="תיאור הצוות" 
                    value={editTeam.description} 
                    onChange={(e) => setEditTeam({...editTeam, description: e.target.value})} 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">מחלקה</label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={editTeam.departmentId}
                      onChange={(e) => setEditTeam({...editTeam, departmentId: e.target.value})}
                    >
                      <option value="">בחר מחלקה</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">מנהל צוות</label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={editTeam.managerId}
                      onChange={(e) => setEditTeam({...editTeam, managerId: e.target.value})}
                    >
                      <option value="">בחר מנהל</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">חברי צוות</label>
                  <select 
                    multiple
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32"
                    value={editTeam.memberIds || []}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setEditTeam({...editTeam, memberIds: selectedOptions});
                    }}
                  >
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">החזק Ctrl (או Cmd) לבחירה מרובה</p>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="editTeamActive"
                    checked={editTeam.isActive}
                    onChange={(e) => setEditTeam({...editTeam, isActive: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="editTeamActive" className="text-sm text-gray-700">צוות פעיל</label>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowEditTeam(false);
                    setEditTeam(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgAdminDashboard;